/**
 * UserMenu — the signed-in super-user card pinned to the bottom of the sidebar.
 * The trigger shows an avatar + email; clicking it opens a popover (above the
 * card) with Configuración (stub), Cuenta y perfil (→ the preferences popup with
 * language + theme), and sign out. Closes on outside-click or Escape.
 * Token-driven — Light + Dark via @obrava/ui semantic tokens, no hardcoded colors.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { BuildVersion, useTheme } from '@obrava/ui';
import { useAuth } from '../auth/AuthProvider.js';
import { Icon, type IconName } from './icons.js';
import { SettingsDialog } from './SettingsDialog.js';

function displayName(email: string): string {
  const local = email.split('@')[0] ?? email;
  return local.charAt(0).toUpperCase() + local.slice(1);
}

function initialsOf(email: string): string {
  return displayName(email).slice(0, 2).toUpperCase();
}

/** A row in the popover that behaves like a button (action / disabled stub). */
function MenuRow(props: {
  readonly icon: IconName;
  readonly label: string;
  readonly onClick?: (() => void) | undefined;
  readonly disabled?: boolean;
  readonly danger?: boolean;
  readonly title?: string | undefined;
}): React.ReactElement {
  const theme = useTheme();
  const color = props.disabled
    ? theme.color.textTertiary
    : props.danger
      ? theme.color.danger
      : theme.color.textPrimary;
  return (
    <button
      type="button"
      role="menuitem"
      disabled={props.disabled}
      title={props.title}
      onClick={props.onClick}
      onMouseEnter={(e) => {
        if (!props.disabled)
          e.currentTarget.style.backgroundColor = props.danger
            ? theme.color.dangerBg
            : theme.color.surfaceSunken;
      }}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.space[3],
        width: '100%',
        padding: `${theme.spacing.space[3]}px ${theme.spacing.space[3]}px`,
        border: 'none',
        background: 'transparent',
        borderRadius: theme.radii.radius.sm,
        color,
        fontFamily: theme.typography.family.sans,
        fontSize: theme.typography.size.footnote,
        fontWeight: theme.typography.weight.medium,
        textAlign: 'left',
        cursor: props.disabled ? 'default' : 'pointer',
        opacity: props.disabled ? 0.6 : 1,
      }}
    >
      <Icon name={props.icon} size={16} />
      {props.label}
    </button>
  );
}

export function UserMenu(props: {
  readonly email: string;
  readonly collapsed: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  const { signOut } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const name = displayName(props.email);

  const divider = (
    <div
      style={{
        height: 1,
        backgroundColor: theme.color.divider,
        margin: `${theme.spacing.space[2]}px ${theme.spacing.space[2]}px`,
      }}
    />
  );

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      {open ? (
        <div
          role="menu"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            minWidth: 232,
            padding: theme.spacing.space[2],
            backgroundColor: theme.color.surfaceOverlay,
            border: `1px solid ${theme.color.border}`,
            borderRadius: theme.radii.radius.card,
            boxShadow: theme.radii.shadow.overlay,
            zIndex: 50,
          }}
        >
          <div
            style={{
              padding: `${theme.spacing.space[2]}px ${theme.spacing.space[3]}px ${theme.spacing.space[3]}px`,
              borderBottom: `1px solid ${theme.color.divider}`,
              marginBottom: theme.spacing.space[2],
            }}
          >
            <div
              style={{
                fontFamily: theme.typography.family.sans,
                fontSize: theme.typography.size.footnote,
                fontWeight: theme.typography.weight.semibold,
                color: theme.color.textPrimary,
              }}
            >
              {name}
            </div>
            <div
              style={{
                fontFamily: theme.typography.family.sans,
                fontSize: theme.typography.size.caption,
                color: theme.color.textTertiary,
              }}
            >
              {props.email}
            </div>
          </div>

          <MenuRow
            icon="settings"
            label={t('platformConsole.common:settings')}
            disabled
            title={t('platformConsole.common:comingSoon')}
          />
          <MenuRow
            icon="account"
            label={t('platformConsole.common:account')}
            onClick={() => {
              setOpen(false);
              setSettingsOpen(true);
            }}
          />

          {divider}

          <MenuRow
            icon="logout"
            label={t('auth:signOut')}
            danger
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
          />

          <div style={{ marginTop: theme.spacing.space[2] }}>
            <BuildVersion
              version={`v ${__BUILD_VERSION__}`}
              copyLabel={t('platformConsole.common:copyVersion')}
              copiedLabel={t('platformConsole.common:versionCopied')}
            />
          </div>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: props.collapsed ? 'center' : 'flex-start',
          gap: theme.spacing.space[3],
          width: '100%',
          padding: theme.spacing.space[2],
          border: 'none',
          backgroundColor: open ? theme.color.hover : 'transparent',
          borderRadius: theme.radii.radius.md,
          cursor: 'pointer',
          fontFamily: theme.typography.family.sans,
          textAlign: 'left',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            flex: '0 0 32px',
            borderRadius: theme.radii.radius.sm,
            backgroundColor: theme.color.primary,
            color: theme.color.onPrimary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: theme.typography.size.caption,
            fontWeight: theme.typography.weight.semibold,
          }}
        >
          {initialsOf(props.email)}
        </div>
        {!props.collapsed ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
              <span
                style={{
                  fontSize: theme.typography.size.footnote,
                  fontWeight: theme.typography.weight.semibold,
                  color: theme.color.textPrimary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {name}
              </span>
              <span
                style={{
                  fontSize: theme.typography.size.caption,
                  color: theme.color.textTertiary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {props.email}
              </span>
            </div>
            <span style={{ flex: '0 0 auto', color: theme.color.textTertiary }}>
              <Icon name="chevron" size={15} strokeWidth={1.9} />
            </span>
          </>
        ) : null}
      </button>

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}
